import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { usercentricsUpdateEventName } from '../../helper/usercentricsCmp';

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  annotationPlugin,
  ChartDataLabels
);

const selector = '[data-dynamic-tariff-chart]';
const fallbackApiBaseUrl = 'https://prod.cockpit-proxy.getag-cloud.com';
const startingDate = '2024-01-01';
const tariffName = 'Dynamischer Tarif';
const allowedPostalCodes = [
  '64625',
  '64665',
  '64673',
  '64404',
  '64686',
  '64646',
  '64342',
  '64653'
];
const chartBarEven = 'rgba(26, 82, 123, 0.9)';
const chartBarOdd = 'rgba(208, 241, 255, 0.9)';
const chartBarCurrent = 'rgba(255, 240, 100, 1)';
const chartBarBorder = 'rgba(26, 82, 123, 1)';
const chartAxisColor = 'rgba(36, 74, 50, 1)';
const fontFamily = 'Theinhardt';
const chartFontWeight = 400;
const chartWidth = 1000;
const chartHeight = 500;
const consentRetryDelays = [0, 150, 500, 1200];
const consentPollInterval = 500;
const consentPollDuration = 60000;

Chart.defaults.font.family = fontFamily;
Chart.defaults.font.weight = chartFontWeight;

type ChartMode = 'EPEX' | 'DYNAMIC';
type PriceType = 'gross' | 'net';

interface UsercentricsServiceInfo {
  categorySlug?: string;
  consent?: {
    status?: boolean | string;
  };
  consentStatus?: boolean | string;
  id?: string;
  name?: string;
  serviceId?: string;
  status?: boolean | string;
  templateId?: string;
}

interface QuarterValue {
  label: number | string;
  qlabel: number | string;
  value: number;
}

interface DayValues {
  hour: QuarterValue[];
  Maximum: number;
  Minimum: number;
}

interface DayData {
  value_gross: DayValues;
  value_net: DayValues;
  [key: string]: any;
}

interface DynamicTariffChartCanvas extends HTMLCanvasElement {
  __dynamicTariffChart?: Chart;
}

export const blockDynamicTariffChart = () => {
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((component) => {
    const apiBaseUrl =
      component.getAttribute('data-dynamic-tariff-api-base-url') ??
      fallbackApiBaseUrl;
    const consentService = component.getAttribute(
      'data-dynamic-tariff-consent'
    );
    const consentTemplateId = component.getAttribute(
      'data-dynamic-tariff-consent-template-id'
    );
    const consentIdentifiers = [consentService, consentTemplateId].filter(
      (value): value is string => Boolean(value)
    );
    let chart: Chart | null = null;
    let epexData: any = null;
    let selectedDate = createInitialDate();
    let mode: ChartMode = 'EPEX';
    let initialized = false;
    let blockedByConsent = false;
    let consentRetryTimeouts: number[] = [];
    let consentPollIntervalId: number | null = null;
    let consentPollTimeoutId: number | null = null;

    const ref = <T extends HTMLElement>(name: string): T => {
      const element = component.querySelector<T>(
        `[data-dynamic-tariff-ref="${name}"]`
      );

      if (!element) {
        throw new Error(`Dynamic tariff chart: missing ref "${name}"`);
      }

      return element;
    };

    const optionalRef = <T extends HTMLElement>(name: string): T | null =>
      component.querySelector<T>(`[data-dynamic-tariff-ref="${name}"]`);

    const action = <T extends HTMLElement>(name: string): T | null =>
      component.querySelector<T>(`[data-dynamic-tariff-action="${name}"]`);

    const init = () => {
      const netInput = optionalRef<HTMLInputElement>('net');

      if (netInput) netInput.checked = true;

      bindConsentEvents();
      buildNoDataField('Keine Daten vorhanden');
      initWhenConsentAllows();

      document.addEventListener(
        usercentricsUpdateEventName,
        scheduleConsentCheck
      );
      window.addEventListener('UC_UI_CMP_EVENT', scheduleConsentCheck);
      window.addEventListener('focus', scheduleConsentCheck);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) scheduleConsentCheck();
      });
    };

    const scheduleConsentCheck = () => {
      consentRetryTimeouts.forEach((timeout) => {
        window.clearTimeout(timeout);
      });

      consentRetryTimeouts = consentRetryDelays.map((delay) =>
        window.setTimeout(initWhenConsentAllows, delay)
      );
    };

    const startConsentPolling = () => {
      if (consentPollIntervalId !== null) return;

      consentPollIntervalId = window.setInterval(
        initWhenConsentAllows,
        consentPollInterval
      );
      consentPollTimeoutId = window.setTimeout(
        stopConsentPolling,
        consentPollDuration
      );
    };

    const stopConsentPolling = () => {
      if (consentPollIntervalId !== null) {
        window.clearInterval(consentPollIntervalId);
        consentPollIntervalId = null;
      }

      if (consentPollTimeoutId !== null) {
        window.clearTimeout(consentPollTimeoutId);
        consentPollTimeoutId = null;
      }
    };

    const initWhenConsentAllows = () => {
      if (!hasConsent()) {
        blockedByConsent = true;
        showConsentOverlay();
        buildNoDataField('Keine Daten vorhanden');
        return;
      }

      hideConsentOverlay();

      if (initialized) {
        if (blockedByConsent) {
          blockedByConsent = false;
          fireRebuild();
        }

        return;
      }

      initialized = true;
      blockedByConsent = false;
      bindEvents();
      setMode('EPEX');
      updateDateControls();
      fireRebuild();
    };

    const hasConsent = () => {
      if (consentIdentifiers.length === 0) return true;

      return (
        areAllUsercentricsConsentsAccepted() ||
        consentIdentifiers.some(
          (identifier) => window.fvUsercentrics?.getConsent(identifier) ?? false
        ) ||
        hasUsercentricsServiceConsent()
      );
    };

    const areAllUsercentricsConsentsAccepted = () => {
      const nativeStatus = window.UC_UI?.areAllConsentsAccepted?.();

      if (typeof nativeStatus === 'boolean') return nativeStatus;

      const services = getUsercentricsServices();

      return services.length > 0 && services.every(hasConsentStatus);
    };

    const hasUsercentricsServiceConsent = () => {
      const servicesList = getUsercentricsServices();

      return servicesList.some((service) => {
        if (!hasConsentStatus(service)) return false;

        return consentIdentifiers.some((identifier) =>
          hasMatchingConsentIdentifier(service, identifier)
        );
      });
    };

    const getUsercentricsServices = () =>
      [
        window.UC_UI?.getServicesBaseInfo?.(),
        window.UC_UI?.getServicesFullInfo?.()
      ].flatMap((services) => flattenUsercentricsServices(services));

    const flattenUsercentricsServices = (
      value: unknown
    ): UsercentricsServiceInfo[] => {
      if (Array.isArray(value)) {
        return value.flatMap((item) => flattenUsercentricsServices(item));
      }

      if (!value || typeof value !== 'object') return [];

      const record = value as Record<string, unknown>;
      const services = isUsercentricsServiceInfo(record)
        ? [record as UsercentricsServiceInfo]
        : [];

      return services.concat(
        Object.values(record).flatMap((item) =>
          flattenUsercentricsServices(item)
        )
      );
    };

    const isUsercentricsServiceInfo = (value: Record<string, unknown>) =>
      'consent' in value ||
      'categorySlug' in value ||
      'name' in value ||
      'templateId' in value;

    const hasConsentStatus = (service: UsercentricsServiceInfo) => {
      const status =
        service.consent?.status ?? service.consentStatus ?? service.status;

      return status === true || status === 'true' || status === 'accepted';
    };

    const hasMatchingConsentIdentifier = (
      value: unknown,
      identifier: string
    ): boolean => {
      if (typeof value === 'string') {
        return value.toLowerCase() === identifier.toLowerCase();
      }

      if (Array.isArray(value)) {
        return value.some((item) =>
          hasMatchingConsentIdentifier(item, identifier)
        );
      }

      if (!value || typeof value !== 'object') return false;

      return Object.values(value).some((item) =>
        hasMatchingConsentIdentifier(item, identifier)
      );
    };

    const bindConsentEvents = () => {
      action<HTMLButtonElement>('consent')?.addEventListener('click', () => {
        startConsentPolling();
        window.UC_UI?.showSecondLayer?.();
      });
    };

    const bindEvents = () => {
      action<HTMLButtonElement>('previous-day')?.addEventListener(
        'click',
        () => {
          stepDay(-1);
        }
      );

      action<HTMLButtonElement>('next-day')?.addEventListener('click', () => {
        stepDay(1);
      });

      action<HTMLButtonElement>('reset')?.addEventListener('click', () => {
        selectedDate = createInitialDate();
        setMode('EPEX');
        clearInputs();
        fireRebuild();
      });

      action<HTMLButtonElement>('calculate')?.addEventListener('click', () => {
        selectedDate = createInitialDate();
        setMode('DYNAMIC');
        fireRebuild();
      });

      optionalRef<HTMLInputElement>('gross')?.addEventListener(
        'change',
        fireRebuild
      );
      optionalRef<HTMLInputElement>('net')?.addEventListener(
        'change',
        fireRebuild
      );

      const postalCodeInput = ref<HTMLInputElement>('postal-code');
      const dropdown = ref<HTMLUListElement>('dropdown');

      postalCodeInput.addEventListener('input', async () => {
        const postalCode = postalCodeInput.value.trim();

        if (postalCode.length === 5) {
          if (!allowedPostalCodes.includes(postalCode)) {
            clearCityDropdown();
            return;
          }

          try {
            const cities = await callCitiesByPostalCode(
              postalCode,
              await callApiKey()
            );
            buildCityDropdown(cities, postalCode);
          } catch (error) {
            buildNoDataField('Keine Orte gefunden');
            console.error(error);
          }

          return;
        }

        if (postalCode.length < 5) clearCityDropdown();
      });

      postalCodeInput.addEventListener('keydown', (event) => {
        if (dropdown.style.display !== 'block') return;

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          dropdown.querySelector<HTMLLIElement>('li')?.focus();
        }
      });

      dropdown.addEventListener('keydown', (event) => {
        const currentItem = document.activeElement;

        if (
          !currentItem ||
          currentItem.tagName !== 'LI' ||
          !dropdown.contains(currentItem)
        ) {
          return;
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          selectCity(currentItem as HTMLLIElement);
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          clearCityDropdown();
          postalCodeInput.focus();
        }
      });
    };

    const setMode = (nextMode: ChartMode) => {
      mode = nextMode;
    };

    function createInitialDate() {
      const date = new Date();
      date.setHours(2, 0, 0, 0);
      return date;
    }

    const getDateString = (date = selectedDate) =>
      date.toISOString().split('T')[0];

    const getDateFormatted = () => {
      const [year, month, day] = getDateString().split('-');
      return `${day}.${month}.${year}`;
    };

    const updateDateControls = () => {
      const today = createInitialDate();
      const previousButton = action<HTMLButtonElement>('previous-day');
      const nextButton = action<HTMLButtonElement>('next-day');

      if (previousButton) {
        previousButton.style.display =
          mode === 'DYNAMIC' && selectedDate.getTime() === today.getTime()
            ? 'none'
            : 'inline-block';
      }

      if (nextButton) {
        const selectedPlusOne = selectedDate.getTime() + 8.64e7;
        nextButton.disabled = selectedPlusOne - today.getTime() > 8.64e7;
      }

      ref<HTMLElement>('date').innerText = getDateFormatted();
    };

    const stepDay = (direction: -1 | 1) => {
      try {
        if (mode === 'DYNAMIC') checkInputsAndReturnInputs();
      } catch {
        return;
      }

      selectedDate.setDate(selectedDate.getDate() + direction);
      updateDateControls();
      fireRebuild();
    };

    const fireRebuild = async () => {
      updateDateControls();

      if (!hasConsent()) {
        buildNoDataField('Keine Daten vorhanden');
        return;
      }

      try {
        const data = await callData();
        await rebuildWithNewParams(retrieveDataForDate(data));
        setFootnote();
      } catch (error) {
        console.error(error);
      }
    };

    const retrieveDataForDate = (data: any) =>
      retrieveData(data?.data ?? data, getDateString());

    const retrieveDataForPriceType = (data: DayData) => {
      try {
        return getPriceType() === 'gross' ? data.value_gross : data.value_net;
      } catch (error) {
        buildNoDataField('Keine Daten verfügbar');
        throw error;
      }
    };

    const retrieveData = (data: any, key: string) => {
      if (data && Object.prototype.hasOwnProperty.call(data, key)) {
        return data[key];
      }

      buildNoDataField('Keine Daten verfügbar');
      throw new Error(`Dynamic tariff chart: missing data key "${key}"`);
    };

    const addVatIfGross = (value: number) =>
      getPriceType() === 'net' ? value : value * 1.19;

    const getPriceType = (): PriceType =>
      ref<HTMLInputElement>('net').checked ? 'net' : 'gross';

    const callData = () =>
      mode === 'EPEX' ? getEpexData() : getDynamicTariffData();

    const callApiKey = async () => {
      let response: Response;

      try {
        response = await fetch(`${apiBaseUrl}/api/v1/adserv`);
      } catch (error) {
        buildNoDataField('Keine Daten vorhanden');
        throw error;
      }

      if (response.ok) {
        const result = await response.json();
        return result.data.apikey;
      }

      buildNoDataField('Keine Daten vorhanden');
      throw new Error('Dynamic tariff chart: API key request failed');
    };

    const callCitiesByPostalCode = async (
      postalCode: string,
      apiKey: string
    ) => {
      const response = await fetch(
        `${apiBaseUrl}/api/v1/ws/locationinfo/getOrteByPlz?plz=${postalCode}`,
        {
          headers: { 'X-API-KEY': apiKey }
        }
      );

      if (!response.ok) {
        throw new Error('Dynamic tariff chart: location request failed');
      }

      const result = await response.json();
      const cities = result.data.ort ?? [];

      return cities.map((city: { ortsname: string }) => city.ortsname);
    };

    const getEpexData = async () => {
      if (hasCachedDate()) return epexData;

      return callEpexData();
    };

    const hasCachedDate = () => {
      if (!epexData) return false;

      try {
        retrieveData(epexData?.data ?? epexData, getDateString());
        return true;
      } catch {
        return false;
      }
    };

    const callEpexData = async () => {
      const apiKey = await callApiKey();
      const response = await fetch(
        `${apiBaseUrl}/api/v1/simplifier/exchangeenergy/quarterly?from=${startingDate}&fractionalDigits=2`,
        {
          headers: { 'X-API-KEY': apiKey }
        }
      );

      if (response.ok) {
        const result = await response.json();
        epexData = result.data;
        return epexData;
      }

      buildNoDataField('Keine Daten vorhanden');
      throw new Error('Dynamic tariff chart: EPEX request failed');
    };

    const getDynamicTariffData = async () => {
      const apiKey = await callApiKey();
      const params = await getRequestParams();
      const response = await fetch(
        `${apiBaseUrl}/api/v1/simplifier/exchangeenergy/quarterly/actions/tariffcalculation?fractionalDigits=2&zipCode=${params.get('postalCode')}&city=${params.get('city')}&primeTimeConsumption=${params.get('primeTimeConsumption')}&rootId=${params.get('rootId')}&from=${params.get('from')}`,
        {
          headers: { 'X-API-KEY': apiKey }
        }
      );

      if (response.ok) {
        const result = await response.json();
        return result.data;
      }

      buildNoDataField('Keine Daten vorhanden');
      throw new Error('Dynamic tariff chart: tariff request failed');
    };

    const callRootIdConfig = async () => {
      const response = await fetch(
        `${apiBaseUrl}/api/v1/config/dynTarifeConfig`
      );

      if (response.ok) {
        const result = await response.json();
        return result.data;
      }

      buildNoDataField('Keine Daten vorhanden');
      throw new Error('Dynamic tariff chart: root ID config request failed');
    };

    const retrieveRootId = (
      config: Record<string, { rootId: string | number }>
    ) => {
      const values = Object.values(config ?? {});
      return values.length > 0 ? values[0].rootId : 0;
    };

    const checkInputsAndReturnInputs = () => {
      const postalCode = ref<HTMLInputElement>('postal-code').value.trim();
      const city = ref<HTMLInputElement>('city').value.trim();

      if (postalCode && !allowedPostalCodes.includes(postalCode)) {
        buildNoDataField(
          'Der GGEW Strom Power Smart ist aktuell nicht in diesem Liefergebiet verfügbar.'
        );
        throw new Error(
          'Dynamic tariff chart: postal code outside service area'
        );
      }

      if (!postalCode || !city) {
        buildNoDataField('Bitte überprüfen Sie Ihre Eingaben');
        throw new Error('Dynamic tariff chart: postal code or city is missing');
      }

      return [postalCode, city];
    };

    const getRequestParams = async () => {
      const [postalCode, city] = checkInputsAndReturnInputs();
      const rootId = retrieveRootId(await callRootIdConfig());

      if (rootId === 0) {
        buildNoDataField('Keine Daten vorhanden');
        throw new Error('Dynamic tariff chart: root ID missing');
      }

      return new Map([
        ['postalCode', postalCode],
        ['city', city],
        ['rootId', rootId],
        ['primeTimeConsumption', 10000],
        ['from', getDateString()]
      ]);
    };

    const rebuildWithNewParams = async (data: DayData) => {
      setChartMetaVisibility(true);

      const selectedValues = retrieveDataForPriceType(data);

      if (!selectedValues.hour || selectedValues.hour.length === 0) {
        buildNoDataField('Keine Daten vorhanden');
        return;
      }

      fillDataFields(selectedValues);
      hideNoDataField();
      await ensureChartFontLoaded();
      chart?.destroy();

      const canvas = ref<HTMLCanvasElement>('chart');
      resetCanvasSize(canvas);
      const context = canvas.getContext('2d');

      if (!context) {
        buildNoDataField('Das Diagramm konnte nicht geladen werden');
        return;
      }

      const labels = selectedValues.hour.map((row) => {
        const label = Number(row.label) - 1;
        return String(row.qlabel) === '1' ? String(label) : '';
      });

      chart = new Chart(context, {
        data: {
          labels,
          datasets: [
            {
              label: '',
              data: selectedValues.hour.map((row) => row.value),
              borderColor: chartBarBorder,
              backgroundColor: setColors(selectedValues.hour),
              borderWidth: 0.2,
              type: 'bar',
              barPercentage: 1,
              categoryPercentage: 1
            }
          ]
        },
        options: {
          layout: {
            padding: {
              top: 20,
              right: 50
            }
          },
          responsive: false,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                autoSkip: false,
                maxRotation: 0,
                minRotation: 0,
                align: 'start',
                color: chartAxisColor,
                font: setFont(15)
              },
              grid: {
                display: false
              },
              title: {
                display: true,
                text: 'Uhrzeit',
                align: 'center',
                color: chartAxisColor,
                font: setFont(17),
                padding: {
                  top: 10
                }
              }
            },
            y: {
              ticks: {
                color: chartAxisColor,
                font: setFont(15)
              },
              beginAtZero: true,
              grid: {
                display: false
              },
              title: {
                color: chartAxisColor,
                display: true,
                text: 'Preis in ct/kWh',
                align: 'center',
                font: setFont(17),
                padding: {
                  top: 20
                }
              }
            }
          },
          plugins: {
            annotation: {
              annotations: {
                yLine: {
                  type: 'line',
                  yMin: 0,
                  yMax: 0,
                  borderColor: 'grey',
                  borderWidth: 1
                }
              }
            },
            datalabels: {
              display: false,
              color: chartBarCurrent,
              anchor: 'end',
              align: 'top',
              font: setFont(12),
              formatter: (value: number) => toDoubleString(value)
            },
            tooltip: {
              enabled: false,
              external: (context) => {
                customTooltip(context, data);
              }
            },
            legend: {
              labels: {
                filter: () => false
              }
            }
          }
        }
      });
      (canvas as DynamicTariffChartCanvas).__dynamicTariffChart = chart;
    };

    const setColors = (data: QuarterValue[]) =>
      data.map((row, index) => {
        const isCurrent = Number(returnIndexOfCurrentData(data)) === index;

        if (isCurrent) return chartBarCurrent;
        return Number(row.label) % 2 === 0 ? chartBarEven : chartBarOdd;
      });

    const fillDataFields = (data: DayValues) => {
      const values = data.hour.map((row) => row.value);
      const currentRow = data.hour[returnIndexOfCurrentData(data.hour)];

      if (!currentRow) {
        emptyDayValues();
        return;
      }

      ref<HTMLElement>('current-value').innerText = toDoubleString(
        currentRow.value
      );
      ref<HTMLElement>('current-time').innerText = formatQuarterTime(
        Number(currentRow.label) - 1,
        Number(currentRow.qlabel)
      );
      ref<HTMLElement>('high-value').innerText = toDoubleString(data.Maximum);
      ref<HTMLElement>('low-value').innerText = toDoubleString(data.Minimum);
      ref<HTMLElement>('average-value').innerText = calculateAverage(values);
    };

    const returnCurrentLabelAndQuarter = () => {
      const [hours, minutes] = new Date()
        .toLocaleTimeString('de', { timeZone: 'Europe/Berlin' })
        .split(':');
      const label = Number(hours) + 1;
      const minuteNumber = Number(minutes);

      if (minuteNumber >= 45) return [label, 4];
      if (minuteNumber >= 30) return [label, 3];
      if (minuteNumber >= 15) return [label, 2];
      return [label, 1];
    };

    const returnIndexOfCurrentData = (data: QuarterValue[]) => {
      const [label, quarterLabel] = returnCurrentLabelAndQuarter();
      const index = data.findIndex(
        (row) =>
          Number(row.label) === label && Number(row.qlabel) === quarterLabel
      );

      return index >= 0 ? index : 0;
    };

    const customTooltip = (context: any, data: DayData) => {
      const tooltipModel = context.tooltip;
      const tooltipElement = getTooltip();

      if (tooltipModel.opacity === 0) {
        tooltipElement.style.opacity = '0';
        return;
      }

      if (!tooltipModel.body) return;

      const tooltipData = context.tooltip.dataPoints[0];
      const priceData =
        getPriceType() === 'gross' ? data.value_gross : data.value_net;
      const row = priceData.hour[tooltipData.dataIndex];
      const table = document.createElement('table');

      tooltipElement.innerHTML = '';
      table.append(
        createTooltipRow(
          `${toDoubleString(tooltipData.formattedValue)} ct/kWh`,
          '20px'
        )
      );
      table.append(
        createTooltipRow(
          formatQuarterTime(Number(row.label) - 1, Number(row.qlabel)),
          '15px'
        )
      );

      if (mode === 'DYNAMIC') {
        const surchargeKey =
          getPriceType() === 'gross'
            ? 'kilowatthourRateMarkup_gross'
            : 'kilowatthourRateMarkup_net';
        const surcharges = retrieveData(data, surchargeKey);
        const epexHourPrice =
          tooltipData.dataset.data[tooltipData.dataIndex] - surcharges;

        table.append(
          createTooltipRow('Energiepreis Spotmarkt: ', null, epexHourPrice)
        );
        table.append(
          createTooltipRow(
            'Netzentgelte: ',
            null,
            addVatIfGross(retrieveData(data, 'gridFeeKilowatthourRate_peak'))
          )
        );
        table.append(
          createTooltipRow(
            'Steuern, Umlagen, Abgaben: ',
            null,
            addVatIfGross(retrieveData(data, 'concessionFee')),
            addVatIfGross(retrieveData(data, 'electricityTax')),
            addVatIfGross(retrieveData(data, 'kwkg_levy')),
            addVatIfGross(retrieveData(data, 'offshoreGrid_levy')),
            addVatIfGross(retrieveData(data, 'par19_gridFee_levy'))
          )
        );
        table.append(
          createTooltipRow(
            'Versorger Zuschlag: ',
            null,
            addVatIfGross(retrieveData(data, 'tariffSurchargeKilowatthourRate'))
          )
        );
      }

      tooltipElement.append(table);

      const position = context.chart.canvas.getBoundingClientRect();
      tooltipElement.style.opacity = '1';
      tooltipElement.style.left = `${position.left + window.scrollX + tooltipModel.caretX}px`;
      tooltipElement.style.top = `${position.top + window.scrollY + tooltipModel.caretY}px`;
    };

    const getTooltip = () => {
      let tooltip = component.querySelector<HTMLDivElement>(
        '.dynamicTariffChart__tooltip'
      );

      if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.className = 'dynamicTariffChart__tooltip';
        component.append(tooltip);
      }

      return tooltip;
    };

    const createTooltipRow = (
      label: string,
      size: string | null,
      ...values: number[]
    ) => {
      const row = document.createElement('tr');
      const labelCell = document.createElement('td');
      const valueCell = document.createElement('td');
      const value =
        values.length > 1
          ? values.reduce((sum, currentValue) => sum + currentValue, 0)
          : values[0];

      if (size) {
        labelCell.style.fontSize = size;
        valueCell.style.fontSize = size;
      }

      labelCell.innerText = label;
      valueCell.innerText = values.length > 0 ? toDoubleString(value) : '';
      row.append(labelCell, valueCell);

      return row;
    };

    const formatQuarterTime = (hour: number, quarter: number) => {
      const paddedHour = hour < 10 ? `0${hour}` : `${hour}`;
      const ranges: Record<number, [string, string]> = {
        1: ['00', '14'],
        2: ['15', '29'],
        3: ['30', '44'],
        4: ['45', '59']
      };
      const [from, to] = ranges[quarter] ?? ranges[1];

      return `${paddedHour}:${from} - ${paddedHour}:${to}`;
    };

    const toDoubleString = (value: number | string | null | undefined) => {
      if (
        value === undefined ||
        value === null ||
        (typeof value === 'number' && Number.isNaN(value))
      ) {
        return '0,00';
      }

      if (typeof value === 'string') {
        const normalized = Number(value.replace(',', '.'));

        if (!Number.isNaN(normalized)) {
          return normalized.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }

        return value.replace(/^(\d+,\d)$/, '$10');
      }

      return value.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    const calculateAverage = (values: number[]) => {
      if (values.length === 0) return '0,00';

      const sum = values.reduce((accumulator, value) => accumulator + value, 0);
      return toDoubleString(sum / values.length);
    };

    const ensureChartFontLoaded = async () => {
      try {
        await document.fonts.load(
          `${chartFontWeight} 17px ${fontFamily}`,
          '0123456789 Uhrzeit Preis ct/kWh'
        );
        await document.fonts.ready;

        if (!document.fonts.check(`${chartFontWeight} 17px ${fontFamily}`)) {
          console.warn(
            `Dynamic tariff chart: font ${fontFamily} is not available for canvas rendering`
          );
        }
      } catch (error) {
        console.warn(
          'Dynamic tariff chart: chart font could not be loaded',
          error
        );
      }
    };

    const resetCanvasSize = (canvas: HTMLCanvasElement) => {
      canvas.style.display = 'block';
      canvas.style.boxSizing = 'border-box';
      canvas.style.width = `${chartWidth}px`;
      canvas.style.height = `${chartHeight}px`;
      canvas.width = chartWidth;
      canvas.height = chartHeight;
    };

    const setFont = (size: number) => ({
      size,
      family: fontFamily,
      weight: chartFontWeight
    });

    const setFootnote = () => {
      const footnote =
        optionalRef<HTMLElement>('info-text')?.querySelector('p');

      if (!footnote) return;

      if (mode === 'DYNAMIC') {
        footnote.innerHTML = `Maximaler Arbeitspreis im Tarif <i>${tariffName}</i> an angegebenem Lieferort. <br><br><b>Zzgl. monatlicher Grundpreis</b> (Servicegebühr, Netzentgelte, Messstellenbetrieb). <br><br>`;
        return;
      }

      footnote.textContent =
        'Reiner Energiepreis Spotmarkt (EPEX SPOT SE Day Ahead) ohne Zuschläge';
    };

    const setChartMetaVisibility = (visible: boolean) => {
      const display = visible ? '' : 'none';
      const priceType = optionalRef<HTMLElement>('price-type');
      const infoText = optionalRef<HTMLElement>('info-text');

      if (priceType) priceType.style.display = display;
      if (infoText) infoText.style.display = display;
    };

    const buildNoDataField = (text: string) => {
      const canvas = optionalRef<HTMLCanvasElement>('chart');
      const noData = ref<HTMLElement>('no-data');

      if (canvas) canvas.style.display = 'none';
      ref<HTMLElement>('no-data-text').innerText = text;
      noData.hidden = false;
      noData.style.display = 'flex';
      setChartMetaVisibility(false);

      emptyDayValues();
    };

    const hideNoDataField = () => {
      const canvas = optionalRef<HTMLCanvasElement>('chart');
      const noData = ref<HTMLElement>('no-data');

      if (canvas) canvas.style.display = '';
      noData.hidden = true;
      noData.style.display = '';
    };

    const showConsentOverlay = () => {
      const consent = optionalRef<HTMLElement>('consent');

      if (!consent) return;

      consent.hidden = false;
      consent.style.display = 'flex';
      startConsentPolling();
    };

    const hideConsentOverlay = () => {
      const consent = optionalRef<HTMLElement>('consent');

      stopConsentPolling();

      if (!consent) return;

      consent.hidden = true;
      consent.style.display = '';
    };

    const emptyDayValues = () => {
      ref<HTMLElement>('current-value').innerText = '-';
      ref<HTMLElement>('current-time').innerText = 'timespan';
      ref<HTMLElement>('high-value').innerText = '-';
      ref<HTMLElement>('low-value').innerText = '-';
      ref<HTMLElement>('average-value').innerText = '-';
    };

    const buildCityDropdown = (cities: string[], postalCode: string) => {
      const dropdown = ref<HTMLUListElement>('dropdown');
      const postalCodeInput = ref<HTMLInputElement>('postal-code');

      dropdown.innerHTML = '';
      dropdown.style.display = 'block';
      dropdown.setAttribute('role', 'listbox');
      postalCodeInput.setAttribute('aria-expanded', 'true');

      cities.forEach((city) => {
        const item = document.createElement('li');

        item.innerText = `${postalCode}, ${city}`;
        item.setAttribute('role', 'option');
        item.setAttribute('tabindex', '-1');
        item.addEventListener('click', () => {
          selectCity(item);
        });

        dropdown.append(item);
      });
    };

    const clearCityDropdown = () => {
      const dropdown = ref<HTMLUListElement>('dropdown');

      dropdown.style.display = 'none';
      dropdown.innerHTML = '';
      ref<HTMLInputElement>('postal-code').setAttribute(
        'aria-expanded',
        'false'
      );
    };

    const selectCity = (item: HTMLLIElement) => {
      const city = item.innerText.split(', ')[1] ?? '';
      const cityField = ref<HTMLElement>('city-field');

      ref<HTMLInputElement>('city').value = city;
      cityField.hidden = false;
      clearCityDropdown();
      ref<HTMLInputElement>('postal-code').focus();
    };

    const clearInputs = () => {
      ref<HTMLInputElement>('postal-code').value = '';
      ref<HTMLInputElement>('city').value = '';
      ref<HTMLElement>('city-field').hidden = true;
      clearCityDropdown();
    };

    init();
  });
};
